import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Switch, Route, useLocation, useHistory, Link } from 'react-router-dom';
import NotFound from './pages/NotFound';
import Icon from './components/Icon';
import Navigation from './components/Navigation';
import { initializeAnalytics, trackPageView } from './utils/analytics';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ModelsProvider } from './contexts/ModelsContext';
import ErrorBoundary from './components/ErrorBoundary';
import PageSkeleton from './components/PageSkeleton';
import CookieConsent from './components/CookieConsent';
import './App.css';

// Lazy-loaded pages — split into separate chunks for faster initial load
const Home = lazy(() => import('./pages/Home'));
const InstallPWA = lazy(() => import('./components/InstallPWA'));
const FeedbackForm = lazy(() => import('./components/FeedbackForm'));
const BackToTop = lazy(() => import('./components/BackToTop'));
const About = lazy(() => import('./pages/About'));
const ModelDetails = lazy(() => import('./pages/ModelDetails'));
const PhotoshootDetails = lazy(() => import('./pages/PhotoshootDetails'));
const LegacyPhotoshootRedirect = lazy(() => import('./pages/LegacyPhotoshootRedirect'));
const ModelsPage = lazy(() => import('./pages/ModelsPage'));
const PhotoshootsPage = lazy(() => import('./pages/PhotoshootsPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const TagPage = lazy(() => import('./pages/TagPage'));
const TagsPage = lazy(() => import('./pages/TagsPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));

// Analytics tracker component that will track page views
const AnalyticsTracker = () => {
  const location = useLocation();
  const history = useHistory();
  const previousPathname = useRef(null);
  
  useEffect(() => {
    // In-page state synced to the URL via history.replace (e.g. photoshoot filters)
    // is not a page view and must not reset the scroll position.
    const isInPageUpdate = history.action === 'REPLACE' && previousPathname.current === location.pathname;
    previousPathname.current = location.pathname;
    if (isInPageUpdate) return;

    window.scrollTo(0, 0);
    trackPageView(location.pathname, document.title);
  }, [location, history]);
  
  return null;
};

function App() {
  // Initialize Google Analytics
  useEffect(() => {
    initializeAnalytics();
  }, []);
  
  return (
    <ThemeProvider>
      <FavoritesProvider>
        <ModelsProvider>
        <Router>
          <div className="App">
            {/* Add InstallPWA component here */}
            <Suspense fallback={null}>
              <InstallPWA />
            </Suspense>
            
            {/* Add the tracker component inside the Router */}
            <AnalyticsTracker />
            
            <header className="App-header">
              <div className="header-inner">
                <Link to="/" className="header-logo" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <img 
                    src="/logo.svg" 
                    alt="Aegyo Models"
                    width="200"
                    height="50"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      document.getElementById('fallback-title').style.display = 'block';
                    }}
                  />
                  <h1 style={{ display: 'none' }} id="fallback-title">Aegyo Models</h1>
                </Link>
                <Navigation />
              </div>
            </header>
            
            <main>
              <ErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
              <Switch>
                <Route exact path="/" component={Home} />
                <Route path="/about" component={About} />
                <Route path="/models" component={ModelsPage} />
                <Route path="/photoshoots" component={PhotoshootsPage} />
                <Route path="/favorites" component={FavoritesPage} />
                <Route exact path="/blog" component={BlogPage} />
                <Route exact path="/blog/search" component={SearchResultsPage} />
                <Route exact path="/blog/categories" component={TagsPage} />
                <Route path="/blog/category/:category" component={CategoryPage} />
                <Route path="/blog/tag/:tag" component={TagPage} />
                <Route path="/blog/:slug" component={BlogPostPage} />
                <Route path="/model/:modelSlug/photoshoot/:photoshootSlug" component={LegacyPhotoshootRedirect} />
                <Route path="/model/:slug" component={ModelDetails} />
                <Route path="/photoshoot/:slug" component={PhotoshootDetails} />
                <Route component={NotFound} />
              </Switch>
              </Suspense>
              </ErrorBoundary>
            </main>
            <footer>
              <div className="footer-content">
                <div className="footer-section footer-brand">
                  <Link to="/" className="footer-logo">
                    <img 
                      src="/logo.svg" 
                      alt="Aegyo Models" 
                      width="40" 
                      height="40"
                    />
                    <span>Aegyo Models</span>
                  </Link>
                  <p className="footer-tagline">Discover stunning photoshoots from Korean models, singers, and actresses.</p>
                </div>
                <div className="footer-section">
                  <h4>Explore</h4>
                  <div className="footer-nav">
                    <Link to="/models">Models</Link>
                    <Link to="/photoshoots">Photoshoots</Link>
                    <Link to="/favorites">Favorites</Link>
                    <Link to="/blog">Blog</Link>
                  </div>
                </div>
                <div className="footer-section">
                  <h4>Company</h4>
                  <div className="footer-nav">
                    <Link to="/about">About</Link>
                    <a href="/privacy-policy.html">Privacy</a>
                    <a href="/terms.html">Terms</a>
                  </div>
                </div>
                <div className="footer-section">
                  <h4>Connect</h4>
                  <div className="social-links">
                    <a href="https://www.facebook.com/aegyomodels" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                      <Icon name="facebook" />
                    </a>
                    <a href="https://bsky.app/profile/aegyomodels.com" target="_blank" rel="noopener noreferrer" aria-label="Bluesky">
                      <Icon name="bluesky" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="footer-bottom">
                <p>&copy; 2016-{new Date().getFullYear()} Aegyo Models. All rights reserved.</p>
              </div>
            </footer>
            <Suspense fallback={null}>
              <FeedbackForm />
              <BackToTop />
            </Suspense>
            <CookieConsent />
          </div>
        </Router>
        </ModelsProvider>
      </FavoritesProvider>
    </ThemeProvider>
  );
}

export default App;